/*
 * Industry content — `en` (international market, source locale).
 *
 * This is the international variant of every industry (the old per-field `*Intl`
 * copy, resolved): generic time-registration framing, no belgium-specific pains/
 * features, and `compliance.slug` / `relatedArticles` pointing only at articles
 * that exist under `/en/` (the 4 market-agnostic posts). `nl.ts` carries the
 * belgium variant. Resolved by `industriesFor("en")` (also the SOURCE_LOCALE
 * fallback). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_TR: Industry[] = [
  {
    slug: "construction",
    name: "İnşaat",
    headline: "İnşaat ekipleri için zaman takibi — konum doğrulamalı giriş, proje saatleri ve bütçe kontrolü, tek bir uygulamada",
    intro: "Ekipler farklı şantiyelere dağılmış, saatler kâğıda karalanmış, taşeron saatlerini mutabık kılmak imkânsız. Done-it her şantiyeye, bordronuzu tek tıkla besleyen kusursuz bir dijital zaman çizelgesi sunar.",
    pains: [
      {
        title: "Kimin nerede çalıştığına dair güvenilir bir kayıt yok",
        body: "Birden fazla aktif şantiye ve dönüşümlü ekiplerle, manuel zaman çizelgeleri doğası gereği hatalıdır. Çalışanlar saatleri yukarı yuvarlar, kayıtları unutur veya günler sonra doldurur — elinizde savunamayacağınız bordro rakamları kalır.",
      },
      {
        title: "Bütçe aşımları çok geç fark edilir",
        body: "Bir şantiyenin planlanan saatlerini tükettiğini fark ettiğinizde, kâr marjı çoktan kaybolmuştur. Canlı bir geri sayım yoktur — yalnızca fatura zamanında kötü bir sürpriz vardır.",
      },
      {
        title: "Bordro hazırlığı her ay saatler alıyor",
        body: "Zaman çizelgelerini toplamak, bunları projelerle mutabık kılmak ve bordro dışa aktarımını hazırlamak günler sürer. Her kayıt potansiyel bir hatadır.",
      },
    ],
    features: [
      {
        title: "Şantiye başına konum doğrulamalı giriş",
        body: "Tek dokunuş çalışanı konum doğrulamalı olarak giriş yaptırır ve saatleri doğru projeye bağlar. Kart okuyucu yok, terminal yok — yalnızca ekibinizin zaten taşıdığı akıllı telefon.",
      },
      {
        title: "Proje başına canlı kalan saat",
        body: "Projeyi açtığınızda planlanan saatleri bir bütçe olarak belirleyin. Çalışanlar kalan saatlerin geri sayımını görür; siz bunu panonuzda canlı olarak görürsünüz. Bütçe aşımları olmadan önce görünür hale gelir.",
      },
      {
        title: "Taşeron yönetimi ve mevcudiyet",
        body: "Bir projeye taşeronları ekleyin, onları girişe atayın ve saatlerini kendi ekibinizle birlikte takip edin — aynı genel görünümde.",
      },
      {
        title: "Tek tıkla bordro dışa aktarımı",
        body: "Kaydedilen tüm saatler — çalışan başına, proje başına — bordro sağlayıcınız için temiz bir tabloya aktarılır. Manuel hazırlık yok, hata yok.",
      },
    ],
    compliance: {
      label: "Dijital zaman kaydı",
      title: "İnşaat şantiyeleri için tasarlanmış konum doğrulamalı giriş",
      body: "Done-it, inşaat ekipleri için konum doğrulamalı giriş, proje bazlı zaman takibi ve temiz bir bordro dışa aktarımı sağlar. Belçika şantiyelerinde faaliyet gösteren ekipler için, işyeri girişini de otomatik olarak besler.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ekip şantiyeye varır", body: "Her çalışan giriş yapmak için bir kez dokunur. GPS konumu doğrular, kaydı zaman damgasıyla işaretler ve saatleri projeye bağlar. Kâğıt yok, terminal yok." },
      { step: "Her şeyi canlı görürsünüz", body: "Saatler, kalan bütçe, taşeron mevcudiyeti ve şantiyeden bildirilen her türlü ek iş — hepsi panonuzda, gerçek zamanlı." },
      { step: "Ay sonu dakikalar içinde", body: "Tüm saatleri çalışan ve proje başına dışa aktarın. Bordro sağlayıcınıza gönderin. Done-it, denetime hazır temiz raporları otomatik olarak üretir." },
    ],
    stats: [
      { value: "1 dokunuş", label: "herhangi bir şantiyeye giriş yapmak için — terminal yok, kâğıt yok" },
      { value: "1 tık", label: "sağlayıcınız için hazır bordro dışa aktarımı" },
      { value: "Canlı", label: "proje ve şantiye başına kalan saat görünümü" },
    ],
    quote: {
      text: "Ekibimiz şantiyede kendileri giriş yapıyor. Ay sonunda bordro kendiliğinden toplanıyor — zaman çizelgelerinin peşinde koşmayı tamamen bıraktık.",
      who: "Sahip, inşaat şirketi · 14 çalışan",
      result: "Haftada idari işlerde 5 saat tasarruf",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Temizlik",
    headline: "Temizlik şirketleri için zaman takibi — konum doğrulamalı giriş, mevcudiyet kanıtı ve bordro tek bir uygulamada",
    intro: "Temizlikçiler gün boyu lokasyonlar arasında hareket eder. Done-it kimin nerede olduğunu gerçek zamanlı takip eder — her lokasyonda konum doğrulamalı giriş, otomatik mola düşümü ve ay sonunda temiz bir bordro dışa aktarımı.",
    pains: [
      {
        title: "Her lokasyonda mevcudiyeti kanıtlamak",
        body: "Müşteriler ve müfettişler kusursuz mevcudiyet kayıtları bekler. Manuel formlara itiraz edilebilir; konum doğrulamalı dijital girişlere edilemez.",
      },
      {
        title: "Ekipler birçok lokasyona dağılmış",
        body: "Temizlikçiler gün boyu lokasyonlar arasında hareket ederken, kimin nerede çalıştığına dair canlı bir görünümünüz olmaz — biri arayana kadar.",
      },
      {
        title: "Yuvarlanan saatlerden kaynaklanan bordro hataları",
        body: "Doğru giriş/çıkış saatleri olmadan, bordro hafızadan yeniden oluşturulur. Saatler yukarı yuvarlanır, maliyetler şişer ve anlaşmazlıklar takip eder.",
      },
    ],
    features: [
      {
        title: "Gerçek zamanlı giriş ve çıkış",
        body: "Çalışanlar her lokasyonda telefonlarından konum doğrulamalı olarak giriş ve çıkış yapar; saat tam olarak, mola kaydıyla birlikte tutulur.",
        plan: "Time Basic",
      },
      {
        title: "Otomatik mola düşümü",
        body: "Zorunlu molalar çalışma gününden otomatik olarak düşülür — manuel hesap yok, anlaşmazlık yok.",
        plan: "Time Basic",
      },
      {
        title: "Çoklu lokasyon canlı genel görünümü",
        body: "Hangi temizlikçinin şu anda hangi lokasyonda olduğunu görün. Müşterilerden önce boşlukları fark edin.",
      },
      {
        title: "Dijital hastalık raporlarıyla devamsızlık kaydı",
        body: "Çalışanlar hastalık raporlarını ve devamsızlık belgelerini doğrudan uygulamadan yükler. Ekibinizin gerçekten kullandığı devamsızlık türlerini oluşturun.",
      },
      {
        title: "Tek tıkla bordro dışa aktarımı",
        body: "Tam zaman damgaları doğrudan sağlayıcınız için bordroya hazır bir dışa aktarıma akar. Kopyalama yok, hata yok.",
      },
    ],
    compliance: {
      label: "Konum doğrulamalı mevcudiyet",
      title: "Her temizlik lokasyonunda gerçek zamanlı mevcudiyet",
      body: "Done-it, temizlik ekiplerine her lokasyonda konum doğrulamalı giriş, otomatik mola düşümü ve şu anda kimin hangi sahada olduğuna dair canlı bir görünüm sunar.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Temizlikçi lokasyona varır", body: "Tek dokunuş onları giriş yaptırır. GPS sahayı kaydeder ve girişi zaman damgasıyla işaretler. Mola kaydı uygulamaya yerleşiktir." },
      { step: "Her lokasyonu canlı görürsünüz", body: "Şu anda kimin hangi sahada olduğu — telefon görüşmesi yok, tahmin yok. Müşterilerden önce kapsama boşluklarını fark edin." },
      { step: "Bordro tek tıktır", body: "Tüm mevcudiyet verileri — tam saatler, lokasyonlar ve molalar — bordro sağlayıcınız için temiz bir şekilde dışa aktarılır." },
    ],
    stats: [
      { value: "1 dokunuş", label: "herhangi bir lokasyona giriş yapmak için — terminale gerek yok" },
      { value: "Canlı", label: "şu anda kimin hangi sahada olduğu görünümü" },
      { value: "0", label: "çift giriş — saatler doğrudan bordroya akar" },
    ],
    quote: {
      text: "Sonunda kimin nerede ve ne zaman temizlik yaptığını kanıtlayabiliyoruz. Artık mevcudiyet formları yok, müşteri anlaşmazlıkları yok — GPS bunu hallediyor.",
      who: "Müdür, temizlik şirketi · 9 çalışan",
      result: "Saatlerde sıfır yukarı yuvarlama",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Montaj",
    headline: "Montaj ekipleri için zaman takibi — proje saatleri, navigasyon ve bütçe kontrolü tek bir uygulamada",
    intro: "Montajcılarınızı ihtiyaç duydukları her şeyle birlikte doğru adrese gönderin, çalışılan her saati otomatik olarak doğru işe bağlayın ve saha kapanmadan önce kalan bütçeyi görün. Done-it, montaj projelerini zamanında ve bütçede tutmanın en basit yoludur.",
    pains: [
      {
        title: "İş bilgisi sahaya zamanında ulaşmıyor",
        body: "Ekipler bir sahaya adres, irtibat kişisi veya kapsam ayrıntıları olmadan varır. O ilk telefon görüşmesi, tek bir saat çalışılmadan önce kaybedilen on dakikalık faturalandırılabilir zamandır.",
      },
      {
        title: "Saatler doğru projeye bağlanmıyor",
        body: "Çalışanlar saatlerini gün sonunda manuel olarak doldurduğunda — eğer doldururlarsa — saatler yanlış işe düşer veya tamamen kaybolur. Faturalar gerçekle örtüşmez.",
      },
      {
        title: "Bütçe aşımları çok geç fark edilir",
        body: "Proje başına kalan saatlerin canlı bir geri sayımı olmadan, bir işin bütçeyi aştığını faturayı kapattığınızda öğrenirsiniz — hâlâ tepki verebileceğiniz zaman değil.",
      },
      {
        title: "Taşeron saatlerini takip etmek zor",
        body: "Aynı sahada çalışan taşeronlar farklı sistemler kullanır, saatleri ayrı doldurur veya hiç doldurmaz. Ay sonunda mutabakat yavaş, hataya açık bir süreçtir.",
      },
    ],
    features: [
      {
        title: "Proje bilgisi doğrudan uygulamaya paylaşılır",
        body: "Çalışanlar projeyi telefonlarında açar: adres, planlanan saatler, irtibat kişisi ve yüklenen dosyalar (planlar, talimatlar, PDF'ler). İhtiyaç duydukları her şey varmadan önce oradadır.",
        plan: "Project Basic",
      },
      {
        title: "Sahaya tek tuşla navigasyon",
        body: "Bir kez dokunun ve telefon proje adresine yönlendirir — uygulamaya yerleşik, ayrı bir harita uygulamasına gerek yok.",
      },
      {
        title: "Proje başına kalan saat geri sayımı",
        body: "Projeyi açtığınızda planlanan saatleri belirleyin. Çalışanlar kaç saat kaldığını görür; siz canlı durumu panonuzda görürsünüz. Aşımlar olmadan önce görünür hale gelir.",
        plan: "Project Basic",
      },
      {
        title: "Proje başına çalışan girişi",
        body: "Her çalışan doğru projeye giriş yapar. Şu anda kimin hangi sahada olduğunu her zaman bilirsiniz.",
        plan: "Project Basic",
      },
      {
        title: "Ek iş ve malzeme kaydı",
        body: "Sahada kapsam genişlediğinde, çalışanlar ek iş ve malzemeleri gerçek zamanlı olarak — metin ve fotoğraflarla — kaydeder, böylece hiçbir şey hafızadan faturalandırılmaz.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Dijital zaman kaydı",
      title: "Montaj ekipleri için proje başına konum doğrulamalı giriş",
      body: "Done-it, montaj ekiplerine proje başına konum doğrulamalı giriş, canlı kalan saat geri sayımı ve otomatik bordro dışa aktarımı sunar — her türlü yerel zaman kaydı gerekliliğini karşılar. Belçika inşaat şantiyelerinde çalışan ekipler için, CheckIn@Work ile de entegre olur.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Çalışan projeyi telefonunda açar", body: "Adres, irtibat, kapsam ve dosya yüklemeleri tam orada. Tek dokunuşla yol tarifi alırlar — yön sormak için ofise telefon yok." },
      { step: "Saatler otomatik olarak doğru işe kaydedilir", body: "Giriş, çalışılan her dakikayı doğru projeye bağlar. Kalan bütçe gerçek zamanlı güncellenir." },
      { step: "Ek iş sahada yakalanır", body: "Kapsam değişiklikleri, ek malzemeler ve gecikmeler oldukları anda metin ve fotoğraflarla kaydedilir — fatura için hazır." },
    ],
    stats: [
      { value: "Gerçek zamanlı", label: "proje başına kalan saat görünümü" },
      { value: "0", label: "saha adresleri için ofis telefonu" },
      { value: "1 tık", label: "uygulamadan herhangi bir proje adresine yol tarifi" },
    ],
    quote: {
      text: "Saatler otomatik olarak doğru projeye düşüyor. İş maliyetlendirmesi birden kolaylaştı — bir işi kapatmadan önce gerçek kâr marjını görebiliyoruz.",
      who: "Sahip, montaj şirketi · 6 çalışan",
      result: "İş başına gerçek maliyet, canlı",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Saha servisi",
    headline: "Saha servisi ekipleri için zaman takibi — kilometre, masraflar ve saatler yolda, hepsi tek bir uygulamada",
    intro: "Günde birden fazla müşteriyi ziyaret eden teknisyenler, torpido gözündeki fişler, hafızadan kaydedilen kilometreler. Done-it saatleri, kilometreleri ve masrafları sahada yakalar — oldukları anda doğru işe bağlı olarak.",
    pains: [
      {
        title: "Kilometreler günler sonra kaydedilir — eğer kaydedilirse",
        body: "Teknisyenlerden rotalarını hafta sonunda yeniden oluşturmalarını istemek, hatalı geri ödemeler, müşterilerle anlaşmazlıklar ve giderek büyüyen bir talep edilmemiş kilometre yığını anlamına gelir.",
      },
      {
        title: "Kaybolan kâğıt fişlerdeki masraflar",
        body: "Torpido gözündeki bir fiş, asla bir faturaya yansımayabilecek bir maliyettir. İş başına gerçek maliyet her zaman olduğundan az hesaplanır.",
      },
      {
        title: "Ekibin nerede olduğuna dair gerçek zamanlı görünüm yok",
        body: "Canlı girişler olmadan, bir teknisyenin nerede olduğunu yalnızca bir şeyler ters gittiğinde ve biri aradığında öğrenirsiniz.",
      },
      {
        title: "İş başına gerçek kâr marjını görmek zor",
        body: "İşçilik, seyahat ve masraflar ayrı sistemlerde takip edildiğinde — veya hiç takip edilmediğinde — iş başına tek bir maliyet rakamı asla olmaz.",
      },
    ],
    features: [
      {
        title: "Otomatik kilometre yakalama (Trax-it)",
        body: "GPS her yolculuk için kalkış, varış ve tam mesafeyi kaydeder. Ulaşım türlerini (kendi aracı, şirket aracı, ortak araç) kendi geri ödeme oranlarıyla yapılandırın. Manuel kayıt defteri yok.",
        plan: "Add-on",
      },
      {
        title: "Sahada masraf yönetimi",
        body: "Çalışanlar sahada bir fişin fotoğrafını çeker; işe bağlanır ve geri ödemeye hazır olur — ay sonunda peşinde koşulmaz.",
        plan: "Time Basic",
      },
      {
        title: "Projeye bağlı maliyet kontrolü",
        body: "Saatler, kilometreler ve masraflar gerçek zamanlı olarak işe yansır. İş başına maliyet, iş hâlâ açıkken görünür.",
        plan: "Project Basic",
      },
      {
        title: "Konum doğrulamalı giriş ve canlı ekip görünümü",
        body: "Her teknisyen müşteri sahasında giriş yapar. Kimin nerede olduğunu şu anda görürsünüz — telefon yok, tahmin yok.",
      },
      {
        title: "Tek tıkla bordro dışa aktarımı",
        body: "Saatler ve kilometre geri ödemeleri bordro sağlayıcınız için temiz bir şekilde dışa aktarılır — yapılandırılmış kilometre oranlarınız otomatik olarak uygulanır.",
      },
    ],
    compliance: {
      label: "Dijital zaman ve kilometre takibi",
      title: "Saha servisi ekipleri için GPS saatleri ve otomatik kilometre",
      body: "Done-it, kilometreyi her yolculuk için otomatik yakalar, yapılandırdığınız geri ödeme oranlarını uygular ve her kilometreyi doğru müşteriye veya projeye kaydeder. AB Tyco kararı, evden ilk müşteriye seyahatin çalışma süresi sayıldığı anlamına gelir — Done-it bunu da takip eder.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Teknisyen ilk çağrıya çıkar", body: "Giriş, yolculuk kaydını başlatır. Kilometreler kalkıştan varışa otomatik olarak yakalanır — manuel giriş yok." },
      { step: "İş sahasında", body: "Saatler doğru projeye bağlanır. Ek işler, malzemeler ve fişler oldukları anda uygulamaya kaydedilir." },
      { step: "Kaos olmadan ay sonu", body: "Her saat, kilometre ve masraf zaten doğru işe bağlanmıştır. Gerçek maliyet rakamlarıyla bordroya ve faturaya aktarın." },
    ],
    stats: [
      { value: "Otomatik", label: "kilometre yakalama — manuel kayıt defteri yok" },
      { value: "Canlı", label: "iş başına maliyet: saatler + seyahat + masraflar" },
      { value: "0", label: "kaybolan kilometre — her yolculuk otomatik yakalanır" },
    ],
    quote: {
      text: "Kilometre eskiden aylık bir baş ağrısıydı. Artık kendiliğinden kaydediliyor — biz daha ofise dönmeden her kilometre doğru müşteriye bağlanmış oluyor.",
      who: "Operasyon müdürü, saha servisi şirketi · 11 çalışan",
      result: "Her seferinde iş başına doğru maliyet",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "İklimlendirme (HVAC)",
    headline: "İklimlendirme teknisyenleri için zaman takibi — iş maliyetlendirmesi, parçalar ve seyahat tek bir uygulamada",
    intro: "Her servis çağrısının üç maliyeti vardır: işçilik, parçalar ve seyahat. Done-it her üçünü de sahada yakalar ve doğru işe yansıtır — böylece faturayı kapatmadan önce kâr marjınızı bilirsiniz.",
    pains: [
      {
        title: "Servis çağrılarını doğru maliyetlendirmek zor",
        body: "İşçilik, parçalar ve seyahat farklı sistemlerde — veya farklı ceplerde — bulunur. Bir çağrının gerçek maliyeti, ancak teknisyen sahadan ayrıldıktan çok sonra netleşir.",
      },
      {
        title: "Servis süresinin kanıtı yok",
        body: "Bir müşteri bir onarımın ne kadar sürdüğüne itiraz ettiğinde, kâğıt zaman çizelgesine itiraz etmek kolaydır. GPS doğrulamalı giriş ve çıkış zaman damgalarına ise değildir.",
      },
      {
        title: "Bakım sözleşmeleri kâr marjını sessizce eritir",
        body: "Yinelenen bakım sözleşmelerinde, küçük aşımlar birçok ziyaret boyunca birikir. Canlı bir saat sayacı olmadan, sözleşmenin zarar ettiğini ancak yenilemede öğrenirsiniz.",
      },
      {
        title: "Teknisyenler molaları kaydetmeyi unutur",
        body: "Manuel zaman girişleri molaları atlar, bu da fazla ödemeye ve gerçekle örtüşmeyen bir bordroya yol açar.",
      },
    ],
    features: [
      {
        title: "Proje bazlı zaman takibi",
        body: "Her servis çağrısı veya bakım sözleşmesi bir projedir. Saatler, seyahat ve malzemeler gerçek zamanlı yansır — size canlı kâr marjı görünürlüğü sağlar.",
        plan: "Project Basic",
      },
      {
        title: "Sahada kaydedilen ek iş ve malzemeler",
        body: "Bir iş büyüdüğünde, teknisyenler ek işleri ve parçaları ayrılmadan önce metin ve fotoğraflarla kaydeder. Hiçbir şey hafızadan faturalandırılmaz.",
        plan: "Project Basic",
      },
      {
        title: "Kilometre ve ulaşım maliyetleri",
        body: "Çağrı başına kilometreler otomatik olarak kaydedilir ve işe maliyetlendirilir. Araç türü başına geri ödeme oranlarını yapılandırın.",
        plan: "Add-on",
      },
      {
        title: "Otomatik mola düşümü",
        body: "Zorunlu molalar toplam süreden otomatik olarak düşülür — anlaşmazlık yok, bordro hatası yok.",
      },
      {
        title: "Konum doğrulamalı giriş ve mevcudiyet kanıtı",
        body: "Her sahada zaman damgalı, GPS doğrulamalı giriş. Teknisyenin ne zaman vardığının ve ayrıldığının tartışılmaz kanıtı.",
      },
    ],
    compliance: {
      label: "Dijital zaman ve maliyet takibi",
      title: "İklimlendirme servis çağrıları için eksiksiz iş maliyetlendirmesi",
      body: "Done-it, iklimlendirme ekiplerine servis çağrısı başına konum doğrulamalı giriş, iş başına canlı maliyet görünürlüğü (işçilik + seyahat + parçalar) ve temiz bir bordro dışa aktarımı sunar — ek donanım olmadan her türlü yerel zaman kaydı gerekliliğini karşılar.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Teknisyen sahada giriş yapar", body: "GPS varışı zaman damgasıyla işaretler ve saatleri servis çağrısına veya bakım sözleşmesine bağlar. Kâğıt yok, terminal yok." },
      { step: "Ek iş anında yakalanır", body: "Kullanılan parçalar, ek saatler ve kapsam değişiklikleri sahadan ayrılmadan önce fotoğraflarla uygulamaya kaydedilir." },
      { step: "İş maliyeti hemen görünür", body: "İşçilik, seyahat ve malzemeler gerçek zamanlı olarak projeye eklenir. Faturayı düzenlemeden önce kâr marjını görürsünüz." },
    ],
    stats: [
      { value: "3", label: "çağrı başına yakalanan maliyet türü: işçilik, seyahat, parçalar" },
      { value: "Canlı", label: "servis sözleşmesi başına kâr marjı görünürlüğü" },
      { value: "0", label: "fatura zamanında sürpriz — maliyetler gerçek zamanlı görünür" },
    ],
    quote: {
      text: "Sonunda her servis sözleşmesinin bize gerçekte ne kadara mal olduğunu biliyoruz. Done-it kullanmaya başladığımız gün kâr marjı sürprizleri sona erdi.",
      who: "Sahip, iklimlendirme şirketi · 7 teknisyen",
      result: "Servis çağrısı başına gerçek maliyet, canlı",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elektrikçiler",
    headline: "Elektrikçiler için zaman takibi — proje başına saatler, bütçe kontrolü ve navigasyon tek bir uygulamada",
    intro: "Proje başına saatler, kalan bütçenin canlı bir geri sayımı ve sahaya tek tuşla navigasyon — idari yük olmadan proje kontrolüne ihtiyaç duyan küçük elektrik ekipleri için tasarlandı.",
    pains: [
      {
        title: "Saatler doğru projeye bağlanmıyor",
        body: "Elektrikçiler bir hafta içinde birden fazla işte çalıştığında, saatleri gün sonunda manuel olarak dağıtmak yanlış atfedilen maliyetlere, faturalandırma anlaşmazlıklarına ve açıklayamayacağınız bir bordroya yol açar.",
      },
      {
        title: "Bütçe aşımları çok geç fark edilir",
        body: "Tüketilen saatlerin bütçelenen saatlere karşı canlı bir görünümü olmadan, bir projenin bütçeyi aştığını faturayı düzenlerken öğrenirsiniz — hâlâ tepki verebileceğiniz zaman değil.",
      },
      {
        title: "Sahayı bulurken zaman kaybı",
        body: "Adresler ayrı bir uygulamaya yazılır, yön için ofise telefon edilir, müşteri sahalarına geç varılır. Boşa harcanan her dakika kaybedilen bir faturalandırılabilir dakikadır.",
      },
      {
        title: "Taşeron saatlerini mutabık kılmak zor",
        body: "Büyük montajlarda, taşeron elektrikçiler kendi saatlerini ayrı takip eder. Bir projenin sonunda işçilik maliyetlerinin birleşik bir görünümünü elde etmek manuel, hataya açık bir iştir.",
      },
    ],
    features: [
      {
        title: "Canlı kalan bütçeli proje saatleri",
        body: "Çalışılan her dakika otomatik olarak doğru projeye bağlanır. Canlı bir geri sayım, bütçeye karşı kaç saat kaldığını gösterir — hem sizin için hem de sahadaki çalışan için.",
        plan: "Project Basic",
      },
      {
        title: "Sahaya tek tuşla navigasyon",
        body: "Proje adresi uygulamadadır. Tek dokunuş navigasyonu açar — arama yok, ayrı bir harita uygulaması yok, ofise telefon yok.",
      },
      {
        title: "Proje başına çalışan girişi",
        body: "Her elektrikçi doğru işe giriş yapar. Şu anda kimin hangi sahada olduğunu her zaman bilirsiniz.",
        plan: "Project Basic",
      },
      {
        title: "Sahada kaydedilen ek iş",
        body: "Kapsam değiştiğinde — beklenmedik bir devre, ek malzemeler — ekip ayrılmadan önce fotoğraflarla uygulamaya kaydedilir.",
        plan: "Project Basic",
      },
      {
        title: "Esnek telafili fazla mesai takibi",
        body: "Fazla mesai oluştuğu anda kaydedilir. Çalışanlar bunun ödenmesini veya izne dönüştürülmesini seçer.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Proje başına konum doğrulamalı giriş",
      title: "Elektrik ekipleri için canlı bütçe kontrolü ve konum doğrulamalı giriş",
      body: "Done-it, elektrikçilere proje başına konum doğrulamalı giriş, canlı kalan saat bütçesi ve bordro dışa aktarımı sunar — her türlü yerel zaman kaydı gerekliliğini karşılar. Belçika inşaat şantiyelerindeki ekipler için, CheckIn@Work ile de entegre olur.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Elektrikçi işe yol tarifi alır", body: "Adres ve kapsam zaten uygulamadadır. Tek dokunuş navigasyonu açar — telefon yok, karışıklık yok." },
      { step: "Saatler otomatik olarak doğru projeye bağlanır", body: "Sahada giriş, çalışılan her dakikayı doğru projeye bağlar. Kalan saat sayacı canlı güncellenir." },
      { step: "Kapsam değişiklikleri kayıtta kalır", body: "Ek iş ve malzemeler sahada, ayrılmadan önce kaydedilir. Fatura gerçekte ne olduğunu yansıtır." },
    ],
    stats: [
      { value: "Canlı", label: "proje başına kalan saat görünümü" },
      { value: "0", label: "saha adresleri için ofis telefonu" },
      { value: "1 dokunuş", label: "herhangi bir sahaya giriş yapmak için — terminale gerek yok" },
    ],
    quote: {
      text: "Bir projenin bütçeyi aşıp aşmadığını gerçek zamanlı biliyoruz. Yalnızca bu bile bu yıl bizi iki kötü sürprizden kurtardı.",
      who: "Sahip, elektrik şirketi · 5 elektrikçi",
      result: "Bütçe aşımları olmadan önce yakalanır",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Tesisat",
    headline: "Tesisat ekipleri için zaman takibi — hızlı giriş, doğru bordro ve her çağrı için mevcudiyet kanıtı",
    intro: "Acil çağrılar, günde birden fazla iş, hafta sonunda hafızadan doldurulan saatler — veya hiç doldurulmayan. Done-it bunu her sahada tek dokunuşluk bir iş ve ay sonunda tek tıklık bir bordro haline getirir.",
    pains: [
      {
        title: "Çağrı saatleri hafızadan yeniden oluşturuluyor",
        body: "Bir günde birkaç işle, tesisatçıların doğru zaman çizelgeleri doldurmaya nadiren vakti olur. Saatler yuvarlanır veya unutulur, bordro en iyi ihtimalle bir tahmindir.",
      },
      {
        title: "Sahadaki sürenin kanıtı yok",
        body: "Bir müşteri bir onarımın ne kadar sürdüğüne itiraz ettiğinde, objektif bir kayıt yoktur. GPS doğrulamalı dijital giriş kolayca üretilebilir — kâğıt zaman çizelgesi ise üretilemez.",
      },
      {
        title: "İşler arası kilometre takip edilmiyor",
        body: "Çağrılar arası seyahat hafta ve ay boyunca birikir, ancak otomatik yakalama olmadan nadiren doğru faturalandırılır veya geri ödenir.",
      },
      {
        title: "Bordro hazırlığı yavaş",
        body: "Ay sonunda, zaman çizelgelerinin peşinde koşmak, hataları düzeltmek ve bordro dışa aktarımını oluşturmak, idari ekibinizin harcamaması gereken zamanı alır.",
      },
    ],
    features: [
      {
        title: "Her çağrıda tek dokunuşla konum doğrulamalı giriş",
        body: "Varış ve ayrılış, lokasyonun konum doğrulamasıyla birlikte tek dokunuşla kaydedilir. Acil çağrılar için yeterince hızlı; anlaşmazlıklar için yeterince hassas.",
        plan: "Time Basic",
      },
      {
        title: "Otomatik mola düşümü",
        body: "Zorunlu molalar toplam süreden otomatik olarak düşülür — doldurulacak bir şey yok, bordro hatası yok.",
        plan: "Time Basic",
      },
      {
        title: "Yolculuk başına yakalanan kilometre",
        body: "Ulaşım türlerini ve geri ödeme oranlarını yapılandırın. Kilometreler otomatik olarak kaydedilir ve doğru müşteriye veya projeye bağlanır.",
        plan: "Add-on",
      },
      {
        title: "Devamsızlık ve hastalık raporu yönetimi",
        body: "Çalışanlar hastalık raporlarını uygulamadan dijital olarak gönderir. Hava koşullarına bağlı gecikmeler dahil, ekibinizin gerçekten kullandığı devamsızlık türlerini oluşturun.",
      },
      {
        title: "Tek tıkla bordro dışa aktarımı",
        body: "Tüm saat ve kilometre verileri bordro sağlayıcınız için temiz bir şekilde dışa aktarılır — manuel hazırlık yok, Excel yok.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Dijital zaman kaydı",
      title: "Her çağrı için hızlı konum doğrulamalı giriş",
      body: "Done-it, tesisat ekipleri için her türlü yerel zaman kaydı gerekliliğini karşılar — çağrı başına tek dokunuşla konum doğrulamalı giriş, otomatik mola düşümü ve temiz bir bordro dışa aktarımı. Kâğıt zaman çizelgesi yok, manuel yeniden oluşturma yok.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Tesisatçı çağrıya varır", body: "Tek dokunuş giriş yaptırır. GPS lokasyonu ve zamanı kaydeder. Acil bir iş için yeterince hızlı." },
      { step: "Çıkışta ayrılış dokunuşu", body: "Çıkış, zaman kaydını kapatır. Sonraki işe kilometre otomatik kaydedilmeye başlar." },
      { step: "Ay sonu tek tıktır", body: "Tüm çağrılar, saatler ve kilometreler temiz bir şekilde dışa aktarılır. Bordro hazırlığı bir günden dakikalara iner." },
    ],
    stats: [
      { value: "1 dokunuş", label: "giriş yapmak için — acil çağrılar için yeterince hızlı" },
      { value: "1 tık", label: "sağlayıcınıza bordro dışa aktarımı" },
      { value: "0", label: "ay sonunda hafızadan yeniden oluşturulan saat" },
    ],
    quote: {
      text: "Eskiden ay sonunda saatleri yeniden oluşturarak bir sabahımızı harcardık. Artık sadece dışa aktarıp gönderiyoruz. Bordro sağlayıcısı temiz bir şekilde alıyor.",
      who: "Sahip, tesisat şirketi · 4 tesisatçı",
      result: "Bordro hazırlığı saatlerden dakikalara indi",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Peyzaj",
    headline: "Peyzaj şirketleri için zaman takibi — mevsimlik ekipler, birden fazla saha ve kilometre tek bir genel görünümde",
    intro: "Yoğun sezon, çok sayıda saha, değişen ekipler ve bir sürü kilometre demektir. Done-it her saati, her devamsızlığı ve her yolculuğu düzenli tutar — böylece evrak işlerine değil, işe odaklanabilirsiniz.",
    pains: [
      {
        title: "Mevsimlik ekipleri yönetmek karmaşık",
        body: "Geçici çalışanlar, değişen programlar ve farklı ekip büyüklükleri, kâğıtla doğru saat takibini neredeyse imkânsız kılar. Ay sonunda mutabakat yaptığınızda, her zaman bir şeyler eksiktir.",
      },
      {
        title: "Saatler birçok sahaya dağılmış",
        body: "Günde üç veya dört saha ziyaret eden bir ekip, doğru bölünmesi gereken saatler üretir. Manuel giriş, yanlış atfetmeye ve itiraz edilen bordroya yol açar.",
      },
      {
        title: "Hava koşulu kaynaklı durmaları belgelemek zor",
        body: "Kötü hava nedeniyle duraklama, peyzajda gerçek bir olaydır. Dijital bir kayıt olmadan, durmaları belgelemek ve ücreti ayarlamak yavaş ve hataya açıktır.",
      },
      {
        title: "Sahalar arası kilometre takip edilmiyor",
        body: "Şirket minibüsleri, kişisel araçlar ve ortak araçlar gün boyu sahalar arasında dolaşır. Otomatik yakalama olmadan, o kilometreler asla maliyet tablosuna girmez.",
      },
    ],
    features: [
      {
        title: "Çoklu saha ekip görünümü",
        body: "Tüm ekibi, tüm sahaları gerçek zamanlı görün. Şu anda kimin nerede çalıştığını bilin — ay sonunda değil.",
      },
      {
        title: "Mevsimlik ekipler için vardiya planlaması",
        body: "Kimin nerede çalışacağını planlayın, son dakika değişikliklerini halledin ve programı ekibin telefonlarına gönderin — herkesi tek tek aramadan.",
        plan: "Time Pro",
      },
      {
        title: "Özel devamsızlık türleri",
        body: "Sektörünüze özel devamsızlık türleri oluşturun: mevsimlik hava durmaları, ebeveyn izni, hastalık günleri. Çalışanlar belgeleri uygulamadan dijital olarak yükler.",
      },
      {
        title: "Ortak araç takibiyle kilometre",
        body: "Ulaşım türlerini (şirket minibüsü, kişisel araç, ortak araç sürücüsü ve yolcusu) kendi oranlarıyla yapılandırın. Kilometreler yolculuk başına otomatik kaydedilir.",
        plan: "Add-on",
      },
      {
        title: "İzne dönüştürmeli fazla mesai takibi",
        body: "Yoğun sezonda fazla mesai hızla birikir. Her çalışan ödeme almayı veya saatleri izin olarak biriktirmeyi seçebilir — şeffaf bir şekilde takip edilir.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Dijital zaman kaydı",
      title: "Mevsimlik peyzaj ekipleri için konum doğrulamalı giriş ve devamsızlık takibi",
      body: "Done-it, peyzaj ekiplerine tüm sahalarda ve mevsimlik personelde dijital zaman kaydı sunar — konum doğrulamalı giriş, hava durmaları için özel devamsızlık türleri ve her türlü yerel gerekliliği karşılayan temiz bir bordro dışa aktarımı.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Ekip ilk sahada giriş yapar", body: "GPS varışı kaydeder. Saatler otomatik olarak doğru lokasyona ve projeye bölünür — gün sonunda manuel dağıtım yok." },
      { step: "Hava işi durdurur", body: "Ustabaşı durmayı uygulamadan kaydeder — temiz bir dijital kayıt, ek evrak işi yok." },
      { step: "Ay sonu: tüm sahalar, tüm ekip, tek dışa aktarım", body: "Her sahadaki ve her ekip üyesindeki saatler, devamsızlıklar ve kilometreler bordro için temiz bir şekilde dışa aktarılır." },
    ],
    stats: [
      { value: "Otomatik", label: "kilometre yakalama — artık kayıt defteri yok" },
      { value: "Canlı", label: "tüm sahalarda ekip görünümü" },
      { value: "1 tık", label: "tüm sahalar ve tüm ekip için bordro dışa aktarımı" },
    ],
    quote: {
      text: "Yoğun sezon eskiden bordro kaosu demekti. Artık ustabaşımız uygulamayı kontrol ediyor, herkes hesaba katılıyor ve ben ayın son günü saatleri dışa aktarıyorum.",
      who: "Sahip, peyzaj şirketi · 12 mevsimlik çalışan",
      result: "Artık ay sonu bordro yeniden oluşturması yok",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Bakım",
    headline: "Bakım ekipleri için zaman takibi — yinelenen saha ziyaretleri, sözleşme başına maliyet ve mevcudiyet kanıtı tek bir uygulamada",
    intro: "Yinelenen bakım sözleşmeleri maliyet görünürlüğüyle ayakta kalır veya çöker. Done-it her ziyareti kaydeder, saatleri sözleşme bütçesine karşı takip eder ve size — ve müşterilerinize — her çağrı için mevcudiyet kanıtı sunar.",
    pains: [
      {
        title: "Bakım sözleşmesi başına maliyet görünürlüğü yok",
        body: "Yinelenen ziyaretlerin saatleri belirli sözleşmeye karşı takip edilmediğinde, bir müşterinin kârlı olup olmadığını yıl sonu değerlendirmesine kadar bilemezsiniz — ki o noktada yeniden fiyatlandırmak için çok geçtir.",
      },
      {
        title: "Müşteriler yapılanın kanıtını bekler",
        body: "Tesis yöneticileri ve mülk sahipleri giderek daha fazla mevcudiyet kaydı ister: kim geldi, ne zaman vardı, ne yaptı. Haftalar sonra sunulan bir kâğıt kayıt, SLA gerekliliklerini karşılamaz.",
      },
      {
        title: "Yinelenen ziyaretleri planlamak ve kaydetmek zor",
        body: "Birbirinin yerine çalışan personelle, birçok yinelenen sahada dönüşümlü vardiyalar, manuel süreçlerin güvenilir şekilde çözemeyeceği bir planlama ve saat takibi bilmecesi yaratır.",
      },
      {
        title: "Hava durmaları dış mekân bakımını da etkiler",
        body: "Bahçe bakımı, cephe bakımı ve dış mekân tesis çalışmaları hava koşullarına bağlı durmalarla karşılaşır. Bunları doğru belgelemek dijital bir iz gerektirir.",
      },
    ],
    features: [
      {
        title: "Sözleşme bazlı maliyet takibi",
        body: "Her bakım sözleşmesi bir projedir. Saatler otomatik olarak buna kaydedilir, size her müşteri için canlı bir maliyet rakamı ve kalan saat geri sayımı sunar.",
        plan: "Project Basic",
      },
      {
        title: "GPS doğrulamalı mevcudiyet kanıtı",
        body: "Zaman damgalı, konum doğrulamalı girişler, kimin sahada, ne zaman ve ne kadar süre olduğuna dair tartışılmaz bir kayıttır — istek üzerine müşterilerle paylaşmaya hazır.",
      },
      {
        title: "Sahadan fotoğraflı güncellemeler",
        body: "Çalışanlar sorunları, tamamlanan görevleri veya ek işleri doğrudan uygulamadan — metin ve fotoğraflarla — bildirir. Müşteriler gerçek zamanlı bir kayıt alır; sizin elinizde faturalandırma için belge olur.",
        plan: "Project Basic",
      },
      {
        title: "Yinelenen vardiya planlaması",
        body: "Tüm sözleşmelerde yinelenen ziyaretleri planlayın, çalışanları atayın ve yerine geçmeleri halledin — herkesi tek tek aramadan.",
        plan: "Time Pro",
      },
      {
        title: "Dış mekân bakımı için devamsızlık türleri",
        body: "Hava durmalarını ve diğer devamsızlık türlerini yapılandırın. Çalışanlar bunları uygulamadan kaydeder; belgeler dijital olarak yüklenir.",
      },
    ],
    compliance: {
      label: "Sözleşme bazlı zaman takibi",
      title: "Bakım sözleşmesi başına canlı maliyet görünürlüğü",
      body: "Done-it, bakım ekiplerine sözleşme başına GPS doğrulamalı mevcudiyet kanıtı, özel devamsızlık türleri ve yinelenen müşteri başına canlı bir maliyet görünümü sunar — ek donanım olmadan her türlü yerel zaman kaydı gerekliliğini karşılar.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Çalışan bakım sahasına varır", body: "Konum doğrulamalı giriş, varış saatini kaydeder ve saatleri doğru sözleşmeye bağlar. Mevcudiyet kanıtı otomatik olarak oluşturulur." },
      { step: "Görevler tamamlanır, sorunlar kaydedilir", body: "Tamamlanan işler ve sorunlar uygulamadan fotoğraflarla bildirilir. Müşteriler tam olarak ne yapıldığını görebilir." },
      { step: "Sözleşme başına maliyet hemen görünür", body: "Saatler sözleşme bütçesine karşı gerçek zamanlı birikir. Sözleşme yenilenmeden önce kâr marjını bilirsiniz." },
    ],
    stats: [
      { value: "Canlı", label: "bakım sözleşmesi başına maliyet" },
      { value: "GPS", label: "talep üzerine müşteriler için hazır mevcudiyet kanıtı" },
      { value: "0", label: "kâr marjını bilmeden yapılan sözleşme yenilemesi" },
    ],
    quote: {
      text: "Bir tesis yöneticisine kimin sahada olduğunu ve ne zaman olduğunu iki dakikada tam olarak söyleyebiliyoruz. Bu düzeyde bir kanıt, müşterilerin bizi görme şeklini değiştirdi.",
      who: "Operasyon müdürü, bakım şirketi · 8 çalışan",
      result: "Müşteri anlaşmazlıkları sıfıra indi",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Mağazalar ve perakende",
    headline: "Mağazalar için mesai takibi — vardiya girişi, yerine geçme ve bordroya hazır dışa aktarım, tek uygulamada",
    intro: "Açılış vardiyaları, geç vardiyalar ve sürekli birinin yerine geçen bir başkası. Done-it her mağazaya kimin hangi vardiyada çalıştığının temiz bir dijital kaydını verir — çalışan kendi telefonundan giriş yapar, ay sonunda bordro hazırdır.",
    pains: [
      {
        title: "Vardiya değişiklikleri bordroya hiç ulaşmıyor",
        body: "Biri hasta olan arkadaşının yerine geçer, biri kapanışta bir saat fazla kalır. Bu ya bir kâğıda not edilir ya da hiç. Bordro daha başlamadan yanlıştır.",
      },
      {
        title: "Birkaç mağaza, tek bordro dönemi",
        body: "Personel şubeler arasında geçiş yapar, yarı zamanlılar iki lokasyonda çalışır. Saatleri mağaza mağaza toplayıp elle birleştirmek her ay bir gün götürür.",
      },
      {
        title: "Molalar ve fazla mesai tahmine kalıyor",
        body: "Yasal molalar hafızadan düşülür, fazla saatler gayriresmî kapatılır — denetimin ilk sorduğu iki şey tam olarak bunlardır.",
      },
    ],
    features: [
      {
        title: "Mağaza içinde mesai başlatma",
        body: "Vardiya başında kendi telefonunda tek dokunuş. Kasada terminal yok, ortak kart yok, depo kapısında panoya asılı liste yok.",
      },
      {
        title: "Mağaza başına saatler, şubeler arası personel",
        body: "Her mağaza bir projedir. Başka bir şubede çalışan kişi o şubeye kaydedilir — saatleri gerçekten çalışıldığı yerde görürsünüz.",
      },
      {
        title: "Molalar otomatik düşülür",
        body: "Mola kuralını bir kez ayarlarsınız, her vardiyaya uygulanır. Fazla mesai gerçekleştiği anda kaydedilir ve her çalışan ödeme mi izin mi istediğini kendisi seçer.",
      },
    ],
    compliance: {
      label: "Vardiya bazlı zaman kaydı",
      title: "Çalışılan her vardiyanın savunulabilir kaydı",
      body: "Done-it her giriş ve çıkışın, molalar ve fazla mesaiyle birlikte nesnel bir dijital kaydını tutar — bir denetimin ya da bordro anlaşmazlığının istediği kaydı.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Personel vardiya girişini yapar", body: "Vardiya başlarken telefonunda tek dokunuş. Başka bir şubedeki destek o şubeye kaydedilir." },
      { step: "Mağaza mağaza kimin işte olduğunu görürsünüz", body: "Tüm lokasyonlarda canlı görünüm — kapanmayan bir açılış vardiyasını kapılar açılmadan fark edersiniz." },
      { step: "Bordro tek dışa aktarımla", body: "Çalışan ve mağaza başına saatler, molalar ve fazla mesai; bordro sağlayıcınıza hazır biçimde dışa aktarılır." },
    ],
    stats: [
      { value: "1 dokunuş", label: "mesai girişi için — kasada terminal yok" },
      { value: "Canlı", label: "tüm mağazalarda kimin işte olduğu görünümü" },
      { value: "0", label: "mağaza ile bordro arasında kaybolan vardiya değişikliği" },
    ],
    quote: {
      text: "İki mağaza ve bir sürü yarı zamanlı çalışan. Eskiden yerine geçme vardiyalarının yarısı hafızadan kapatılıyordu. Şimdi her saat doğru mağazada ve bordro dakikalar sürüyor.",
      who: "İşletme sahibi, perakende · 11 çalışan",
      result: "Bordro hazırlığı bir günden bir saatin altına indi",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Ofisler ve profesyonel hizmetler",
    headline: "Ofisler için zaman takibi — müşteri ve proje başına saatler, tablo dosyası olmadan",
    intro: "Mali müşavirler, noterler, mimarlar, ajanslar ve danışmanlıklar zamanlarını faturalandırır — ya da en azından nereye gittiğini bilmek zorundadır. Done-it saatleri doğru müşteri ve projeye kaydeder; masa başında da yolda da.",
    pains: [
      {
        title: "Faturalanabilir saatler cuma günü hatırlanıyor",
        body: "Hafta sonunda yazılan zaman, tahmin edilen zamandır. İşle fatura arasında saatler sessizce kaybolur.",
      },
      {
        title: "Bir müşterinin gerçek maliyeti bilinmiyor",
        body: "Ücret aylar önce konuşuldu. İşin hâlâ o bütçeye sığıp sığmadığı, ancak biri oturup topladığında ortaya çıkar — genelde çok geç.",
      },
      {
        title: "Hibrit çalışma kaydı delik deşik ediyor",
        body: "Ekibin bir kısmı ofiste, bir kısmı evde, bir kısmı müşteride. Devam bir olgu olmaktan çıkıp yeniden kurguya dönüşür.",
      },
    ],
    features: [
      {
        title: "Müşteri ve proje bazında saatler",
        body: "Her kayıt bir projeye düşer; müşteri başına saatleri hiçbir şey dışa aktarmadan, kimseye sormadan görürsünüz.",
      },
      {
        title: "Masa başında da saha dışında da çalışır",
        body: "Telefonda ya da tarayıcıda aynı uygulama. Müşteri ziyareti o müşteriye çalışılan zaman olarak sayılır, isterseniz yol süresiyle birlikte.",
      },
      {
        title: "Fazla mesai ve izin tek yerde",
        body: "Fazla saatler gerçekleştiği anda kaydedilir, izinler uygulamada talep edilip onaylanır — kimin ne zaman izinli olduğunu tutan ayrı bir tablo dosyası yok.",
      },
    ],
    compliance: {
      label: "Müşteri bazlı zaman kaydı",
      title: "Çalışılan saatlerin nesnel kaydı",
      body: "Done-it çalışan başına çalışma süresinin dijital ve değişiklik izi bırakan bir kaydını tutar — hem bordronun hem de savunulabilir bir müşteri faturasının temeli.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Bir müşteri projesi açın", body: "Müşteriyle birlikte bir kez kurun; bütçeye göre faturalandırıyorsanız planladığınız saatleri de girin." },
      { step: "Ekip zamanını o projeye kaydeder", body: "Masa başından ya da telefondan. Her saat birinin hafızasına değil, bir müşteriye düşer." },
      { step: "Müşteri başına saatleri görün, bordro için dışa aktarın", body: "Proje başına canlı toplamlar ve ay sonunda bordro ya da faturalandırma için temiz bir dışa aktarım." },
    ],
    stats: [
      { value: "Müşteri başına", label: "saatler, iş daha sürerken görünür" },
      { value: "Ofis ya da yol", label: "aynı kayıt, telefon ve tarayıcı" },
      { value: "1 dışa aktarım", label: "bordro ve faturalandırma için" },
    ],
    quote: {
      text: "Eskiden her cuma tüm haftayı yeniden kurardık. Şimdi saatler zaten doğru müşteride ve hangi dosyanın ücreti yediğini görebiliyorum.",
      who: "Ortak, mali müşavirlik bürosu · 8 çalışan",
      result: "Her ay geri kazanılan faturalanabilir saatler",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salonlar ve kişisel bakım",
    headline: "Salonlar için mesai takibi — vardiyalar, yarı zamanlı saatler ve kâğıt ajanda olmadan bordro",
    intro: "Kuaförler, berberler, tırnak ve güzellik stüdyoları vardiya ve yarı zamanlı sözleşmelerle döner. Done-it kimin ne zaman çalıştığını kaydeder, molaları otomatik düşer ve bordroya temiz bir dosya verir.",
    pains: [
      {
        title: "Yarı zamanlı sözleşmeler, sonu gelmeyen varyasyonlar",
        body: "Bir hafta üç gün, ertesi hafta dört, cumartesi yerine salı. Sözleşmedeki saatlerle çalışılan saatler hızla birbirinden ayrılır.",
      },
      {
        title: "Randevu defteri bir zaman kaydı değildir",
        body: "Randevular neyin rezerve edildiğini söyler, kimin çalıştığını değil. Bordro ya da denetim sorduğunda rezervasyon sistemi yanlış cevaptır.",
      },
      {
        title: "Fazla saatler gayriresmî kapatılıyor",
        body: "Son müşteri için geç kalmak hatırlanır, söz verilir, bazen de unutulur. Anlaşmazlığa dönüşen boşluk tam olarak budur.",
      },
    ],
    features: [
      {
        title: "Salonda mesai başlatma",
        body: "Vardiya başında kendi telefonunda tek dokunuş — resepsiyonda ortak terminal yok, aynanın yanında imza listesi yok.",
      },
      {
        title: "Mola ve yarı zamanlı kuralları bir kez",
        body: "Mola kuralını ve standart saatleri bir kez ayarlayın; her vardiyaya uygulanır, sözleşmesi farklı olanlar için kişiye özel istisnalarla.",
      },
      {
        title: "Fazla mesai: ödeme ya da izin",
        body: "Fazla saatler çalışıldığı anda kaydedilir ve her çalışan ödeme mi karşılık izin mi istediğini seçer — hafızayla değil, uygulamada kapanır.",
      },
    ],
    compliance: {
      label: "Dijital zaman kaydı",
      title: "Randevu defterinin ötesinde ayakta kalan bir kayıt",
      body: "Done-it çalışan başına gerçek çalışma süresini molalar ve fazla mesaiyle birlikte kaydeder — rezervasyonlardan bağımsız ve kontrol edildiğinde savunulabilir.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Personel vardiya girişini yapar", body: "Gelişte tek dokunuş. Molalar bir kez belirlediğiniz kurala göre düşülür." },
      { step: "Günü akarken görürsünüz", body: "Kim işte, kim molada, kim geç kaldı — sormadan, canlı olarak." },
      { step: "Bordro temiz bir dosya alır", body: "Çalışan başına çalışılan saatler, molalar ve fazla mesai; bordro sağlayıcınız için dışa aktarılır." },
    ],
    stats: [
      { value: "1 dokunuş", label: "mesai girişi için — ortak cihaz yok, kâğıt yok" },
      { value: "Otomatik", label: "her vardiyada mola düşümü" },
      { value: "0", label: "hafızadan kapatılan geç saat" },
    ],
    quote: {
      text: "Herkes yarı zamanlı ve her hafta farklı. Uygulama bunu olduğu gibi kaydediyor — ayı tablo dosyasında yeniden kurmayı bıraktım.",
      who: "İşletme sahibi, kuaför salonu · 6 çalışan",
      result: "Ay sonunda tartışmalı saat kalmadı",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Sağlık ve wellness",
    headline: "Spor salonları, stüdyolar ve spalar için mesai takibi — vardiyalar, dersler ve bordro tek uygulamada",
    intro: "Spor salonları, fitness stüdyoları, fizyoterapi merkezleri ve spalar uzun açılış saatleriyle ve dönüşümlü personelle çalışır. Done-it hangi vardiyayı kimin devraldığını kaydeder, molaları otomatik düşer ve bordroyu temiz biçimde dışa aktarır.",
    pains: [
      {
        title: "Uzun açılış saatleri, dönüşümlü destek",
        body: "Sabah vardiyaları, akşam dersleri ve hafta sonları; tam zamanlı, yarı zamanlı ve serbest çalışan karışımıyla doldurulur. Çizelge bir plandır, kayıt değil.",
      },
      {
        title: "Ders saatleri ile salon saatleri iç içe geçiyor",
        body: "Bir eğitmen iki ders verir, aralarda resepsiyona bakar. Bunu doğru ödemek, planlananı değil gerçekte çalışılanı bilmeyi gerektirir.",
      },
      {
        title: "Birkaç lokasyon, tek idari süreç",
        body: "Personel stüdyolar arasında geçiş yapar. Her ay saatleri elle birleştirmek hem yavaş hem hataya açıktır.",
      },
    ],
    features: [
      {
        title: "Stüdyo bazında mesai girişi",
        body: "Çalıştıkları lokasyonda telefonlarında tek dokunuş. Başka bir stüdyodaki destek o stüdyonun saatlerine düşer.",
      },
      {
        title: "Kimin işte olduğuna dair canlı görünüm",
        body: "Tüm lokasyonlarda salonu gerçek zamanlı görün — boş kalan bir sabah vardiyası kapılar açıldıktan sonra değil, önce görünür.",
      },
      {
        title: "Molalar ve fazla mesai hallolur",
        body: "Mola kuralları otomatik uygulanır, fazla saatler gerçekleştiği anda kaydedilir; her çalışan ödeme ya da karşılık izin seçer.",
      },
    ],
    compliance: {
      label: "Çok lokasyonlu vardiya kaydı",
      title: "Her vardiya ve her lokasyon için nesnel bir kayıt",
      body: "Done-it çalışan ve lokasyon başına çalışma süresinin dijital kaydını tutar — doğru ödemenin ve çalışılan saatlere dair her kontrolün temeli.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Personel stüdyoda mesai girişi yapar", body: "Vardiya başında, çalıştıkları lokasyonda tek dokunuş." },
      { step: "Destek canlı olarak görünür", body: "Şu anda hangi lokasyonda kimin olduğu bellidir; boşluklar üyeler fark etmeden doldurulur." },
      { step: "Bordro için tek dışa aktarım", body: "Çalışan ve stüdyo başına saatler, molalar ve fazla mesai; bordro sağlayıcısına hazır." },
    ],
    stats: [
      { value: "Stüdyo başına", label: "saatler, personel başka yerde çalışsa bile" },
      { value: "Canlı", label: "lokasyonlar genelinde salon görünümü" },
      { value: "Otomatik", label: "molalar ve fazla mesai, elle hesap yok" },
    ],
    quote: {
      text: "İki stüdyo, uzun açılış saatleri ve bir sürü yerine geçme vardiyası. Şimdi saatler doğru lokasyona düşüyor ve bordro tek dosya.",
      who: "Müdür, fitness stüdyosu · 14 çalışan",
      result: "Çizelge ve bordro nihayet birbirini tutuyor",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Konaklama ve yeme-içme",
    headline: "Kafeler, restoranlar ve oteller için mesai takibi — vardiyalar, molalar ve fazla mesai adilce kapanır",
    intro: "Bölünmüş vardiyalar, geç kapanışlar ve uzayan yoğun bir cumartesi. Done-it saatleri gerçekte çalışıldığı gibi kaydeder, molaları sizin kuralınıza göre düşer ve bordroya tek temiz bir dosya verir.",
    pains: [
      {
        title: "Vardiya hiçbir zaman çizelgedeki saatte bitmiyor",
        body: "Servis uzar, kapanış yarım saat daha alır. O dakikalar gerçek iştir ve hafızadan kapatılması anlaşmazlıkların başlangıcıdır.",
      },
      {
        title: "Bölünmüş vardiyaları kaydetmek zor",
        body: "Öğle servisi, bir mola, sonra akşam servisi. Kâğıt üzerinde bu ya tek uzun bloğa ya da iki tahmine dönüşür — ikisi de gerçek değildir.",
      },
      {
        title: "Yüksek devir, sürekli işe alım",
        body: "Her sezon yeni personel. Eğitim gerektiren hiçbir sistem ilk yoğun haftadan sağ çıkmaz.",
      },
    ],
    features: [
      {
        title: "Her servis için giriş ve çıkış",
        body: "Her bloğun başında ve sonunda tek dokunuş; bölünmüş vardiyalar olduğu gibi kaydedilir — tek blokta birleştirilmez.",
      },
      {
        title: "Molalar sizin kuralınıza göre",
        body: "Zorunlu molalar otomatik düşülür, sözleşmesi gerektirenler için kişiye özel istisnalarla. Ay sonunda elle hesap yok.",
      },
      {
        title: "Fazla mesai uygulamada kapanır",
        body: "Kapanıştan sonraki fazladan yarım saat çalışıldığı anda kaydedilir ve ödeme olarak ya da karşılık izin olarak kapanır — seçim çalışanın.",
      },
    ],
    compliance: {
      label: "Bölünmüş vardiya zaman kaydı",
      title: "Devri yüksek bir ekipte savunulabilir saat kaydı",
      body: "Done-it her vardiyanın, molanın ve fazla saatin nesnel bir dijital kaydını tutar — bir bordro kontrolünün ya da iş müfettişliğinin görmek istediği tam olarak budur.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Personel servis için giriş yapar", body: "Bloğun başında tek dokunuş, sonunda aynısı. Bölünmüş vardiyalar bölünmüş kalır." },
      { step: "Salonu canlı görürsünüz", body: "Bu akşamki servis için kim işte, kim hâlâ molada, kim kapanışı geçti." },
      { step: "Bordro gerçek saatleri alır", body: "Çalışan başına çalışılan süre, molalar ve fazla mesai; tek dosyada dışa aktarılır." },
    ],
    stats: [
      { value: "Bölünmüş vardiya", label: "tek tahmin değil, iki blok olarak kaydedilir" },
      { value: "Otomatik", label: "kuralınıza göre mola düşümü" },
      { value: "1 dakika", label: "yeni başlayanı sisteme almak için — kendi telefonu" },
    ],
    quote: {
      text: "Kapanış her zaman uzar. Şimdi o dakikalar tartışılmak yerine kaydediliyor ve personel karşılığında izin kullanabiliyor.",
      who: "İşletme sahibi, restoran · 17 çalışan",
      result: "Daha az anlaşmazlık, daha hızlı bordro",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Sağlık ve bakım hizmetleri",
    headline: "Bakım ekipleri için mesai takibi — vardiyalar, ev ziyaretleri ve yetkinlikler tek kayıtta",
    intro: "Muayenehaneler, evde bakım ekipleri ve bakımevleri vardiyalar, ziyaretler ve sıkı kayıt tutma etrafında döner. Done-it çalışılan süreyi vardiya ve ziyaret bazında kaydeder, çalışan belgelerini de bulabileceğiniz yerde tutar.",
    pains: [
      {
        title: "Ziyaretler ve vardiyalar ayrı sistemlerde",
        body: "Ev ziyaretleri bir araçta, çizelgedeki saatler başka birinde, bordro ise üçüncü bir sürümü alır. Bunları birbirine oturtmak aylık bir iş.",
      },
      {
        title: "Gece ve hafta sonu saatleri yanlış fiyatlanıyor",
        body: "Farklı saatler farklı ücret taşır. Kayıt yaklaşıksa ödeme de yaklaşıktır — düzeltme ise sonra gelir.",
      },
      {
        title: "Sertifikalar sessizce geçerliliğini yitiriyor",
        body: "Bakım alanında yetkinlikler ve sertifikalar önemlidir; ama bir denetim sorana kadar kimsenin açmadığı bir klasörde durur.",
      },
    ],
    features: [
      {
        title: "Vardiya ya da ziyaret bazında giriş",
        body: "Vardiya başında ya da evde bakımda her müşteri adresinde tek dokunuş — kanıt gereken yerde konum doğrulamalı.",
      },
      {
        title: "Saatler ödediğiniz şekilde ayrılır",
        body: "Gece, hafta sonu ve normal saatler ayrı ayrı kaydedilir; böylece bordro dışa aktarımı işin gerçekte nasıl ödendiğini zaten yansıtır.",
      },
      {
        title: "Çalışan belgeleri tek yerde",
        body: "Sözleşmeler, sertifikalar ve kimlikler ait oldukları kişinin yanında saklanır — biri istediğinde saniyeler içinde bulunur.",
      },
    ],
    compliance: {
      label: "Vardiya ve ziyaret kaydı",
      title: "Denetimden sağ çıkan kayıtlar",
      body: "Done-it çalışan başına çalışma süresinin dijital ve nesnel bir kaydını tutar — saatler, molalar ve gece/hafta sonu ayrımları — her kişiye ait belgelerle birlikte.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Vardiya ya da ziyaret için giriş yapın", body: "Lokasyonda ya da müşterinin adresinde tek dokunuş; kanıt gereken yerde konum doğrulamalı." },
      { step: "Saatler doğru kalemde toplanır", body: "Normal, gece ve hafta sonu saatleri gerçekleştikçe ayrı ayrı kaydedilir." },
      { step: "Bordro için dışa aktarın", body: "Dönem başına tek temiz dosya, zaten ödediğiniz şekilde ayrılmış olarak." },
    ],
    stats: [
      { value: "Ziyaret başına", label: "ya da vardiya başına — ekibiniz nasıl çalışıyorsa" },
      { value: "Ayrı", label: "gece ve hafta sonu saatleri, çalışıldığı gibi kaydedilir" },
      { value: "Arşivde", label: "çalışan başına saklanan sertifikalar" },
    ],
    quote: {
      text: "Ev ziyaretleri ve çizelgedeki vardiyalar nihayet aynı kayıtta. Bordro artık bir mutabakat işi olmaktan çıktı.",
      who: "Koordinatör, evde bakım ekibi · 22 çalışan",
      result: "Üç kayıt yerine tek kayıt",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
